import * as _ from 'lodash';
import {
    FrontEndConfigFormField,
    FrontEndConfigFormModel,
} from 'app/overview/config/configuration_tab/general/frontend_config/FrontEndConfigFormModel';
import { IFormFieldModel } from 'app/overview/config/configuration_tab/general/frontend_config/IFormFieldModel';
import { EventRegistry } from 'app/shared/events/event_registry';

interface SingleValueFormFieldViewState<T> {
    valueUnderEdit: T;
    isDirty: boolean;
    isValid: boolean;
    isBusy: boolean;
}

export abstract class SingleValueFormFieldModel<T>
    implements IFormFieldModel<SingleValueFormFieldViewState<T>>
{
    private readonly _destroyListener;
    private _frontEndConfigFormFieldModel;
    private readonly _formField: FrontEndConfigFormField;
    protected _origValue: T;
    protected _valueUnderEdit: T;
    private _onChange: EventRegistry;
    private _hasBeenInitialised: boolean = false;
    private _isBusy: boolean = false;

    constructor(
        frontEndConfigFormModel: FrontEndConfigFormModel,
        formField: FrontEndConfigFormField,
    ) {
        this._frontEndConfigFormFieldModel = frontEndConfigFormModel;
        this._formField = formField;
        this._onChange = new EventRegistry();
        this._destroyListener = frontEndConfigFormModel.onFieldChange(formField, (newValue) => {
            this._origValue = this.convertValueForEditing(newValue.data);
            if (!this._hasBeenInitialised) {
                this._hasBeenInitialised = true;
                this.revert();
            } else {
                this._notifyListeners();
            }
        });
        // self-add, will self-remove when destroy() is called
        this._frontEndConfigFormFieldModel.addFormFieldModel(this);
    }

    getId(): string {
        return this._formField.toString();
    }

    abstract setValue(val: any);

    revert(): void {
        this._valueUnderEdit = this._origValue;
        this._notifyListeners();
    }

    /**
     *
     * @param callback callback func
     * @returns {() => void} destroy listener anon func
     */
    onChange(callback): () => void {
        const result = this._onChange.addEventListener(callback);
        this._notifyListeners();
        return result;
    }

    getViewState(): SingleValueFormFieldViewState<T> {
        return {
            valueUnderEdit: this._valueUnderEdit,
            isDirty: this.isDirty(),
            isValid: this.isValid(),
            isBusy: this._isBusy,
        };
    }

    isValid(): boolean {
        // Default to true!
        return true;
    }

    isDirty(): boolean {
        return !_.isEqual(this._valueUnderEdit, this._origValue);
    }

    async save(): Promise<any> {
        try {
            this._isBusy = true;
            this._notifyListeners();
            return await this._frontEndConfigFormFieldModel.saveFormField(
                this._formField,
                this.convertValueForSaving(this._valueUnderEdit),
            );
        } finally {
            this._isBusy = false;
            this._notifyListeners();
        }
    }

    destroy(): void {
        this._destroyListener();
        this._frontEndConfigFormFieldModel.removeFormFieldModel(this);
    }

    /**
     * Sometimes we need to convert types. e.g. a number to a string
     * in preparation for editing.
     * @param value
     * @returns {any}
     */
    convertValueForEditing(value: any) {
        return value;
    }

    /**
     * The symmetric method for convertValueForEditing()
     * @param value
     * @returns {any}
     */
    convertValueForSaving(value: any) {
        return value;
    }

    protected _notifyListeners() {
        this._onChange.notifyListeners(this.getViewState());
    }
}
